/*
 * Copyright (c) 2022 Omnicell. All rights reserved.
 *
 * This program contains proprietary, trade secret information of
 * Omnicell. and may not be copied, reproduced or reverse engineered
 * in any way without the prior written permission of Omnicell.
 */

import { uniqWith } from 'lodash';
import moment from 'moment';
import 'moment/locale/es';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import Slider from 'react-slick';
import { defaultCarouselSettings } from '../../../constants/carouselSettings';
import { DATA } from '../../../constants/messageTypes';
import { DataContext } from '../../../contexts/DataContext';
import {
  disableAllCardActions,
  sendMessageToBot,
  unregisterTimeslotGridUpdater
} from '../../../utils/messaging-helpers';
import AppointmentDateCard from './AppointmentDateCard';

const AppointmentDateCarousel = ({ clinic, flowIdentifier, language }) => {
  const [canRender, setCanRender] = useState(true);
  const [selectedAppointmentDate, setSelectedAppointmentDate] = useState(null);
  const { currentFlowIdentifier } = useContext(DataContext);
  const carouselRef = useRef(null);

  useEffect(() => {
    if (flowIdentifier !== currentFlowIdentifier) {
      setCanRender(false);
    }
  }, [flowIdentifier, currentFlowIdentifier]);

  // A new carousel mounting means a new date-selection flow — clear any stale timeslot grid updater
  // so the next date selection renders a fresh TimeSlotGrid rather than updating an old one
  useEffect(() => {
    unregisterTimeslotGridUpdater();
  }, []);

  // add available time slot config dates to array (API returns month number accurately, moment is 0 indexed, so subtract 1 for conversion)
  const timeSlotConfigDates = useMemo(
    () =>
      clinic.location.timeslotConfigs.map(c =>
        moment({ ...c.startTime.date, month: c.startTime.date.month - 1 }).format('YYYY-MM-DD')
      ),
    [clinic]
  );

  // Derive unique future dates and the index of the first date that has configs
  const dateDefs = useMemo(() => {
    const today = moment().startOf('day');
    const sortedTimeSlotDates = [...timeSlotConfigDates].sort();
    const uniqueDates = uniqWith(sortedTimeSlotDates, (a, b) => a === b).filter(
      day => !moment(day).isBefore(today)
    );
    let firstDateWithConfigs = null;
    uniqueDates.forEach((date, i) => {
      if (firstDateWithConfigs === null && timeSlotConfigDates.some(d => d === date)) {
        firstDateWithConfigs = i;
      }
    });
    return { uniqueDates, firstDateWithConfigs };
  }, [timeSlotConfigDates]);

  const cards = useMemo(
    () =>
      dateDefs.uniqueDates.map((date, i) => {
        const datePieces = moment(date).locale(language).format('ddd MMM D').split(' ');
        const dateHasConfigs = timeSlotConfigDates.some(d => d === date);
        return (
          <AppointmentDateCard
            date={date}
            isSelected={selectedAppointmentDate === date}
            isDisabled={selectedAppointmentDate !== null}
            setSelectedAppointmentDate={setSelectedAppointmentDate}
            dayOfWeek={datePieces[0]}
            month={datePieces[1]}
            dayNumber={datePieces[2]}
            dateHasConfigs={dateHasConfigs}
            key={i}
          />
        );
      }),
    [dateDefs, language, selectedAppointmentDate]
  );

  const settings = useMemo(() => {
    if (!dateDefs.uniqueDates.length) return null;
    return {
      ...defaultCarouselSettings,
      slidesToScroll: dateDefs.uniqueDates.length >= 5 ? 4 : 1,
      arrows: dateDefs.uniqueDates.length >= 5,
      centerMode: false,
      infinite: false,
      initialSlide: dateDefs.uniqueDates.length >= 4 ? dateDefs.firstDateWithConfigs ?? 0 : 0
    };
  }, [dateDefs]);

  useEffect(() => {
    if (canRender && timeSlotConfigDates.includes(selectedAppointmentDate)) {
      disableAllCardActions();
      sendMessageToBot(DATA, {
        // TODO: send the action to get ID dynamically
        data: { date: selectedAppointmentDate },
        id: 'date_select'
      });
    }
  }, [selectedAppointmentDate]);

  // Handle accessibility after date list is available and rendered
  useEffect(() => {
    if (dateDefs.uniqueDates.length > 0 && carouselRef.current) {
      // Wait for react-slick to finish rendering
      setTimeout(() => {
        // Set aria-hidden for all slides
        const dateSlides = carouselRef.current.querySelectorAll('.slick-slide');
        dateSlides.forEach(element => {
          element.setAttribute('aria-hidden', false);
        });

        // Focus first available date card
        const firstAvailableCard = carouselRef.current.querySelector(
          '.appointment-date-wrapper:not(.disabled)'
        );
        if (firstAvailableCard) {
          firstAvailableCard.focus();
        }

        // scrollIntoView
        carouselRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }, 300);
    }
  }, [dateDefs]);

  return settings && canRender ? (
    <div className="appointment-date-carousel-wrapper" ref={carouselRef}>
      <Slider {...settings}>{cards}</Slider>
    </div>
  ) : null;
};

export default React.memo(AppointmentDateCarousel);
